import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, useEffect, useState } from 'react';

import { CatalogCurators } from '@/components/CatalogCurators/CatalogCurators';
import CatalogPageConsole from '@/components/CatalogPageConsole/CatalogPageConsole';
import { CoverLotsWideComponent } from '@/components/CoverLots/CoverLotsWideComponent';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogIsDone,
    getCatalogIsTimedPlus,
    getCatalogTitle,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogDetailCurators } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { getCatalogUrl } from '@/utils/urls';

import CatalogStatus from './components/CatalogStatus/CatalogStatus';
import CatalogInfo from './components/Info/CatalogInfo';

type Props = {
    catalogId: number;
};

const CatalogComponent = ({ catalogId }: Props) => {
    const [forceIsLive, setForceIsLive] = useState(false);
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const catalogUrl = getCatalogUrl(catalogId, title);
    const catalogCurators = useAppSelector((state) => getCatalogDetailCurators(state, catalogId)) || [];
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const isCatalogDone = useAppSelector((state) => getCatalogIsDone(state, catalogId));
    const showLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const wide = useIsWiderThan(1023);
    const [locked, setUnlock] = useState(true);

    useEffect(() => {
        const timeout: ReturnType<typeof setTimeout> = setTimeout(() => setUnlock(false), 500);
        return () => {
            clearTimeout(timeout);
        };
    }, []);

    const curatorCount = catalogCurators.length;

    return (
        <div
            className={cn(
                'mb-lg flex w-[calc(100%-40px)] max-w-[1240px] flex-col self-center border-b border-border-secondary',
                'print:overflow-visible',
                'xsMax:mb-sm',
                isTimedPlus && 'border-t border-border-secondary'
            )}
            suppressHydrationWarning
        >
            {!isTimedPlus &&
                (showLive ? (
                    <CatalogPageConsole catalogId={catalogId} />
                ) : (
                    <CoverLotsWideComponent
                        catalogId={catalogId}
                        catalogUrl={catalogUrl}
                    />
                ))}
            <div
                className={cn(
                    'flex w-full flex-row justify-between border-t border-border-secondary',
                    'xsMax:w-full xsMax:flex-[0_1_auto] xsMax:flex-col xsMax:border-0 xsMax:border-t-0',
                    locked && 'max-h-[323px] overflow-hidden'
                )}
            >
                <CatalogInfo
                    catalogId={catalogId}
                    forceIsLive={forceIsLive}
                />
                {Boolean(curatorCount) && wide && (
                    <CatalogCurators
                        catalogId={catalogId}
                        className={cn(
                            'flex flex-row justify-around',
                            'xsMax:flex-[0_1_auto] xsMax:flex-col xsMax:px-md',
                            curatorCount > 1 ? 'min-w-[300px]' : 'min-w-[250px]'
                        )}
                    />
                )}
                {!isCatalogDone && (
                    <div className="min-w-[290px] max-w-full shrink grow-0 basis-[290px]">
                        {/* div added so component manages its own height,
                            outside of the expand collapse functionality. */}
                        <CatalogStatus
                            catalogId={catalogId}
                            forceIsLive={forceIsLive}
                            setForceIsLive={setForceIsLive}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo<Props>(CatalogComponent);
